/**
 * What a 3D plan pane says about its drawing (`spec-onboarding-3d-plan-renderer-switch`, R2.3 and
 * R2.4), shaped like `underlayStatusView`. Pure, so the wording is pinned by a unit test.
 *
 * - **drawing**: nothing for the first two seconds, so a normal load never flashes a message; then
 *   a polite "Drawing the floor plan…".
 * - **failed**: assertive, with a body by cause. Retry is offered only where drawing again can help
 *   (`map-error`, `timeout`): no token and a browser without WebGL fail the same way again, and a
 *   renderer that could not load (`load`, `konva-removal` R4.2) needs the page reloaded.
 * - **compact** (the Observers corner plan): the title alone, for its size.
 */
import type { BannerTone } from '@repo/design-system';
import type { PlanDrawState } from './planDrawState';

/** R2.4: how long a drawing plan shows nothing before it says it is drawing. */
export const PLAN_DRAWING_REVEAL_MS = 2_000;

export const PLAN_DRAW_FAILED_TITLE = 'The floor plan could not be drawn.';
export const PLAN_DRAWING_TITLE = 'Drawing the floor plan…';
export const PLAN_DRAW_RETRY_LABEL = 'Retry drawing the floor plan';

export interface PlanDrawStatusView {
  tone: BannerTone;
  role: 'status' | 'alert';
  title: string;
  body?: string;
  /** The Retry button's accessible name; absent when drawing again cannot help. */
  retryLabel?: string;
}

const FAILED_BODY = {
  'no-token': 'The map service is not set up for this account. Ask TwinLabs support.',
  webgl:
    'This browser could not start 3D drawing. Turn on hardware acceleration or try another browser.',
  'map-error': 'Something stopped the map loading.',
  timeout: 'Something stopped the map loading.',
  load: 'Something stopped the floor plan loading. Reload the page to try again.',
} as const;

export function planDrawStatusView(
  state: PlanDrawState,
  { elapsedMs, compact = false }: { elapsedMs: number; compact?: boolean },
): PlanDrawStatusView | null {
  if (state.kind === 'drawn') return null;
  if (state.kind === 'drawing') {
    // A compact pane (the Observers corner) stays blank while it draws, as it always has: it has
    // no room for a status line, and it speaks only when it fails.
    if (compact || elapsedMs < PLAN_DRAWING_REVEAL_MS) return null;
    return { tone: 'info', role: 'status', title: PLAN_DRAWING_TITLE };
  }
  if (compact) return { tone: 'error', role: 'alert', title: PLAN_DRAW_FAILED_TITLE };
  const retries = state.cause === 'map-error' || state.cause === 'timeout';
  return {
    tone: 'error',
    role: 'alert',
    title: PLAN_DRAW_FAILED_TITLE,
    body: FAILED_BODY[state.cause],
    ...(retries ? { retryLabel: PLAN_DRAW_RETRY_LABEL } : {}),
  };
}
